import { useEffect, useMemo, useState } from "react";
import { ShieldCheck, Leaf, Sparkles, HeartHandshake } from "lucide-react";
import { api } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import ProductCard from "@/components/ProductCard";
import BirthSetsCompare from "@/components/BirthSetsCompare";

const isMerino = (p) => /merino|lana/i.test(`${p.title} ${p.material || ""} ${(p.materials || []).join(" ")}`);

const TABS = [
  { id: "cotone", label: "100% cotone turco" },
  { id: "merino", label: "Lana merino" },
];

const POINTS = {
  cotone: [
    { icon: Sparkles, title: "Completi di tutto", text: "Ogni corredino ha già tutto quello che serve per i primi giorni: niente da cercare, niente da abbinare." },
    { icon: ShieldCheck, title: "100% cotone turco certificato", text: "Fibra lunga, morbida e traspirante, controllata e certificata: solo cotone a contatto con la pelle." },
    { icon: HeartHandshake, title: "Pensati per la sua pelle", text: "Cuciture morbide e tessuti delicati, perfetti per la pelle sottile di un neonato." },
  ],
  merino: [
    { icon: Leaf, title: "Il calore della lana merino", text: "Fibra finissima che scalda senza pesare e regola la temperatura: ideale per l'autunno e l'inverno." },
    { icon: ShieldCheck, title: "Sempre con una tutina in cotone", text: "Ogni corredino in merino comprende una tutina in 100% cotone da mettere a contatto con la pelle, per evitare qualsiasi irritazione." },
    { icon: Sparkles, title: "Completi di tutto", text: "Tutto quello che serve per l'arrivo a casa, coordinato e pronto da mettere in valigia." },
  ],
};

export default function BirthSets() {
  const { profile, addToWishlist, inWishlist } = useProfile();
  const [all, setAll] = useState(null);
  const [tab, setTab] = useState("cotone");

  useEffect(() => {
    const sex = ["femmina", "maschio"].includes(profile?.sex) ? profile.sex : undefined;
    api.get("/catalog", { params: { category: "Corredini", sex } })
      .then(({ data }) => setAll(data.products || [])).catch(() => setAll([]));
  }, [profile?.sex]);

  const list = useMemo(() => (all || []).filter((p) => (tab === "merino" ? isMerino(p) : !isMerino(p)))
    .sort((a, b) => Number(!!b.available) - Number(!!a.available) || Number(!!b.best_seller) - Number(!!a.best_seller) || (Number(b.beauty) || 0) - (Number(a.beauty) || 0))
    .slice(0, 10), [all, tab]);
  const hasMerino = (all || []).some(isMerino);

  useEffect(() => {
    if (all?.length && window.location.hash === "#corredini-nascita") {
      setTimeout(() => document.getElementById("corredini-nascita")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [all]);

  if (all && !all.length) return null;

  return (
    <section id="corredini-nascita" data-testid="birth-sets" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12 scroll-mt-16">
      <div className="rounded-3xl bg-white border border-[#EAE3DC] p-5 sm:p-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4E7363]">I nostri corredini nascita</div>
        <h2 className="font-serif text-2xl sm:text-3xl text-[#2D2825] mt-1">Tutto pronto per il suo arrivo</h2>
        <p className="text-sm text-[#5C534E] mt-2 max-w-2xl">
          Dopo tutto quello che hai imparato, ecco i nostri corredini nascita: completi di tutto, in 100% cotone turco
          certificato. Per i mesi freddi abbiamo anche quelli in lana merino, sempre con una tutina in 100% cotone da
          mettere sulla pelle.
        </p>
        {hasMerino && (
          <div className="mt-5 inline-grid grid-cols-2 gap-1 rounded-2xl bg-[#F4EFEA] p-1" data-testid="birth-sets-tabs">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`birth-sets-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`rounded-xl px-4 h-10 text-sm transition-colors ${tab === t.id ? "bg-white text-[#2D2825] shadow-sm font-medium" : "text-[#8C7F78]"}`}>
                {t.label}
              </button>
            ))}
          </div>
        )}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid={`birth-sets-points-${tab}`}>
          {POINTS[tab].map((pt) => (
            <div key={pt.title} className="flex gap-3 rounded-2xl bg-[#F6F9F7] border border-[#DCE7E0] p-3.5">
              <pt.icon size={18} className="shrink-0 text-[#4E7363] mt-0.5" />
              <div>
                <div className="text-sm font-medium text-[#2D2825]">{pt.title}</div>
                <div className="text-[12.5px] leading-snug text-[#5C534E] mt-0.5">{pt.text}</div>
              </div>
            </div>
          ))}
        </div>
        {hasMerino && <BirthSetsCompare all={all} isMerino={isMerino} />}
        <div className="loved-rail mt-6" data-testid="birth-sets-rail">
          {all === null && <div className="min-h-[360px]" />}
          {list.map((p, i) => (
            <ProductCard key={p.id} product={p} onAdd={addToWishlist} inList={inWishlist(p.id)}
              delay={Math.min(i, 5) * 0.04} testid={`birth-set-card-${p.id}`} />
          ))}
        </div>
      </div>
    </section>
  );
}
